import { Suspense } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import {
    IconCheckDefault,
    IconClockOutline,
    IconRocket,
    IconTimesDefault
} from '@iwoca/icons';
import { Pill } from '@iwoca/orion';
import { AlertBox, Text } from '@iwoca/orion-ui';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { useGetCreditBuilderData } from './useGetCreditBuilderData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';

export const CreditBuilderProgress = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => (
    <ErrorBoundary fallback={() => <FallbackErrorModal />}>
        <Suspense fallback={<MainContentPending />}>
            <CreditBuilderContent accountId={accountId} />
        </Suspense>
    </ErrorBoundary>
);

const CreditBuilderContent = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const { activeStep } = useGetCreditBuilderData(accountId);
    return (
        <div className="col-span-5 h-fit pt-xl max-sm:col-span-full max-sm:pt-m">
            <div className="gap-s pb-xl">
                <Text className="font-med">
                    <FormattedMessage
                        id="_.credit_builder_widget.credit_builder_title"
                        defaultMessage="Your credit journey"
                    />
                </Text>
                <Text className="text-s">
                    <FormattedMessage
                        id="_.credit_builder_widget.credit_builder_subtitle"
                        defaultMessage="Improve your credit with regular repayments to demonstrate your financial reliability and graduate toward our prime flexible lending."
                    />
                </Text>
            </div>

            <div className="grid grid-cols-1 gap-m">
                <CreditBuilderStep
                    stepNumber={1}
                    isActive={activeStep === 1}
                    isComplete={activeStep > 1}
                    isNextStep={false} //first step will always be false
                    stepLabel={
                        <FormattedMessage
                            id="_.credit_builder_widget.3_month_micro_loan_title"
                            defaultMessage="3 month credit builder loan"
                        />
                    }
                    stepFundingMaxAmount={3_800}
                    stepDurationMaxAmount={3}
                    stepIsTopUpEligible={false}
                />

                <CreditBuilderStep
                    stepNumber={2}
                    isActive={activeStep === 2}
                    isComplete={activeStep > 2}
                    isNextStep={activeStep + 1 === 2}
                    stepLabel={
                        <FormattedMessage
                            id="_.credit_builder_widget.6_month_micro_loan_title"
                            defaultMessage="6 month credit builder loan"
                        />
                    }
                    stepFundingMaxAmount={7_500}
                    stepDurationMaxAmount={6}
                    stepIsTopUpEligible={false}
                />
                <CreditBuilderStep
                    stepNumber={3}
                    isActive={activeStep === 3}
                    isComplete={activeStep > 3}
                    isNextStep={activeStep + 1 === 3}
                    stepLabel={
                        <FormattedMessage
                            id="_.credit_builder_widget.flexi_loan_title"
                            // eslint-disable-next-line formatjs/no-emoji
                            defaultMessage="🎉 Fully flexible loan"
                        />
                    }
                    stepFundingMaxAmount={1_000_000}
                    stepDurationMaxAmount={60}
                    stepIsTopUpEligible={true}
                />
            </div>

            <div>
                <AlertBox className="mt-l pl-xl text-s font-reg">
                    <FormattedMessage
                        id="_.credit_builder_widget.credit_builder_info_text"
                        defaultMessage="Building creditworthiness isn't always a linear journey so these steps will depend on our credit assessments of your business at the time."
                    />
                </AlertBox>
            </div>
        </div>
    );
};

const CreditBuilderStep = ({
    isComplete = false,
    isActive = false,
    isNextStep = false,
    stepLabel,
    stepNumber,
    stepFundingMaxAmount,
    stepDurationMaxAmount,
    stepIsTopUpEligible
}: {
    isComplete: boolean;
    isActive: boolean;
    stepNumber: number;
    stepLabel: React.ReactNode;
    stepFundingMaxAmount: number;
    stepDurationMaxAmount: number;
    stepIsTopUpEligible: boolean;
    isNextStep: boolean;
}) => (
    <div
        className={twMerge(
            classNames(
                'grid grid-cols-7 rounded-m bg-structure-100 py-m',
                {
                    'bg-primary-20': isActive
                },
                { 'bg-structure-95': isComplete }
            )
        )}
        data-testid={`step-${stepNumber}`}
    >
        {/* Step Number */}
        <div className="col-span-1 mx-auto text-structure-40">
            <div
                className={twMerge(
                    classNames(
                        'relative flex size-[30px] items-center justify-center rounded-full bg-structure-80',
                        {
                            'bg-structure-80': isComplete
                        },
                        {
                            'size-[30px] border-2 border-structure-100 bg-primary-20':
                                isActive
                        }
                    )
                )}
            >
                <Text
                    className={twMerge(
                        classNames('text-m font-med text-structure-100', {
                            'text-primary-10': isComplete
                        })
                    )}
                >
                    {stepNumber}
                </Text>
            </div>
        </div>
        {/* End Step Number */}

        <div
            className={twMerge(
                classNames('col-span-5', { 'col-span-4': isComplete })
            )}
        >
            <Text
                className={twMerge(
                    classNames(
                        'mb-m text-s font-bold text-structure-60',
                        {
                            'text-structure-100': isActive
                        },
                        {
                            'my-auto text-primary-10': isComplete
                        }
                    )
                )}
            >
                {isActive ? (
                    <span>
                        <FormattedMessage
                            id="_.credit_builder_widget.current_step"
                            defaultMessage="You're here:"
                        />{' '}
                    </span>
                ) : isNextStep ? (
                    <span>
                        <FormattedMessage
                            id="_.credit_builder_widget.next_step"
                            defaultMessage="Next milestone:"
                        />{' '}
                    </span>
                ) : null}
                {stepLabel}
            </Text>

            <div className="grid grid-cols-1 gap-m min-m:grid-cols-3">
                <StepFeature
                    icon={
                        <IconRocket
                            className={twMerge(
                                classNames('fill-structure-60 text-xs', {
                                    'fill-structure-100': isActive
                                })
                            )}
                        />
                    }
                    label={
                        <FormattedMessage
                            id="_.credit_builder_widget.funding_max_amount"
                            defaultMessage="Up to {fundingMaxAmount, number, :: currency/GBP}"
                            values={{
                                fundingMaxAmount: stepFundingMaxAmount
                            }}
                        />
                    }
                    isActive={isActive}
                    isComplete={isComplete}
                />
                <StepFeature
                    icon={
                        <IconClockOutline
                            className={twMerge(
                                classNames('fill-structure-60 text-xs', {
                                    'fill-structure-100': isActive
                                })
                            )}
                        />
                    }
                    label={
                        <FormattedMessage
                            id="_.credit_builder_widget.duration_max_amount"
                            defaultMessage="Up to {stepDurationMaxAmount} months"
                            values={{ stepDurationMaxAmount }}
                        />
                    }
                    isActive={isActive}
                    isComplete={isComplete}
                />
                <StepFeature
                    icon={
                        stepIsTopUpEligible ? (
                            <IconCheckDefault
                                className={twMerge(
                                    classNames('fill-structure-60 text-xs', {
                                        'fill-structure-100': isActive
                                    })
                                )}
                            />
                        ) : (
                            <IconTimesDefault
                                className={twMerge(
                                    classNames('fill-structure-60 text-xs', {
                                        'fill-structure-100': isActive
                                    })
                                )}
                            />
                        )
                    }
                    label={
                        <FormattedMessage
                            id="_.credit_builder_widget.top_up"
                            defaultMessage="TopUp in-loan"
                        />
                    }
                    isActive={isActive}
                    isComplete={isComplete}
                />
            </div>
        </div>

        {isComplete && (
            <div className="col-span-full mr-l text-center max-sm:col-start-4 min-m:col-span-2">
                <Pill size="small" className="bg-success-lt">
                    <Text className="p-xs text-xs font-med text-success-drk">
                        <FormattedMessage
                            id="_.credit_builder_widget.fully_repaid"
                            defaultMessage="Fully repaid"
                        />
                    </Text>
                </Pill>
            </div>
        )}
    </div>
);

const StepFeature = ({
    icon,
    label,
    isActive,
    isComplete
}: {
    icon: React.ReactNode;
    label: React.ReactNode;
    isActive: boolean;
    isComplete: boolean;
}) => {
    if (isComplete) {
        return null;
    }
    return (
        <div className="flex flex-row items-center justify-start gap-xs">
            {icon}

            <Text
                className={twMerge(
                    classNames('text-xs text-structure-60', {
                        'text-structure-100': isActive
                    })
                )}
            >
                {label}
            </Text>
        </div>
    );
};
